import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { I18nProvider } from './context/I18nContext';
import { LANG_CODES } from './lib/langs';
import TopBar from './components/TopBar';
import Header from './components/Header';
import Footer from './components/Footer';
import DashboardLayout from './components/DashboardLayout';
import AdminLayout from './components/AdminLayout';
import HomePage from './pages/HomePage';
import HomeInsurance from './pages/HomeInsurance';
import HealthInsurance from './pages/HealthInsurance';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import InsurerArticle from './pages/InsurerArticle';
import Contact from './pages/Contact';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DashboardHome from './pages/dashboard/DashboardHome';
import MyPolicies from './pages/dashboard/MyPolicies';
import MyQuotes from './pages/dashboard/MyQuotes';
import Documents from './pages/dashboard/Documents';
import Renewals from './pages/dashboard/Renewals';
import Messages from './pages/dashboard/Messages';
import Support from './pages/dashboard/Support';
import AdminOverview from './pages/admin/AdminOverview';
import AdminUsers from './pages/admin/AdminUsers';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminPolicies from './pages/admin/AdminPolicies';
import AdminMessages from './pages/admin/AdminMessages';
import AdminContent from './pages/admin/AdminContent';
import AdminBlog from './pages/admin/AdminBlog';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminSEO from './pages/admin/AdminSEO';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminStorage from './pages/admin/AdminStorage';
import AdminInsurers from './pages/admin/AdminInsurers';
import AdminCampaignReport from './pages/admin/AdminCampaignReport';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PublicShell({ children }) {
  return (
    <>
      <TopBar />
      <Header />
      {children}
      <Footer />
    </>
  );
}

/**
 * Wraps :lang-prefixed routes. If the :lang segment is not a valid non-English
 * supported code, redirect to the same path without the prefix (English).
 */
function LangGate() {
  const { lang } = useParams();
  const location = useLocation();
  if (!LANG_CODES.includes(lang) || lang === 'en') {
    // strip the invalid /lang segment and redirect
    const stripped = location.pathname.replace(new RegExp(`^/${lang}`), '') || '/';
    return <Navigate to={stripped + location.search + location.hash} replace />;
  }
  return <Outlet />;
}

// The public routes tree — reused under both "/" and "/:lang" so translations render
// on prefixed URLs like /es/home-insurance, /fr/blog/my-post, etc.
const publicRouteDefs = [
  { path: '',                  element: <PublicShell><HomePage /></PublicShell>, index: true },
  { path: 'home-insurance',    element: <PublicShell><HomeInsurance /></PublicShell> },
  { path: 'health-insurance',  element: <PublicShell><HealthInsurance /></PublicShell> },
  { path: 'about',             element: <PublicShell><About /></PublicShell> },
  { path: 'faq',               element: <PublicShell><FAQ /></PublicShell> },
  { path: 'blog',              element: <PublicShell><Blog /></PublicShell> },
  { path: 'blog/:slug',        element: <PublicShell><BlogPost /></PublicShell> },
  { path: 'insurers/:slug',    element: <PublicShell><InsurerArticle /></PublicShell> },
  { path: 'contact',           element: <PublicShell><Contact /></PublicShell> },
  { path: 'login',                 element: <PublicShell><Login /></PublicShell> },
  { path: 'forgot-password',       element: <PublicShell><ForgotPassword /></PublicShell> },
  { path: 'reset-password/:token', element: <PublicShell><ResetPassword /></PublicShell> },
];

const renderPublicRoutes = () => publicRouteDefs.map((r) =>
  r.index
    ? <Route key="_index" index element={r.element} />
    : <Route key={r.path} path={r.path} element={r.element} />
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <I18nProvider>
            <AuthProvider>
              <ScrollToTop />
              <Routes>
                {/* English (unprefixed) public site */}
                {renderPublicRoutes()}

                {/* Language-prefixed public site (/es/, /fr/, /de/, …) */}
                <Route path=":lang" element={<LangGate />}>
                  {renderPublicRoutes()}
                </Route>

                {/* User dashboard (no language prefix — auth area) */}
                <Route element={<><TopBar /><Header /><DashboardLayout /></>}>
                  <Route path="/dashboard"            element={<DashboardHome />} />
                  <Route path="/dashboard/policies"   element={<MyPolicies />} />
                  <Route path="/dashboard/quotes"     element={<MyQuotes />} />
                  <Route path="/dashboard/documents"  element={<Documents />} />
                  <Route path="/dashboard/renewals"   element={<Renewals />} />
                  <Route path="/dashboard/messages"   element={<Messages />} />
                  <Route path="/dashboard/support"    element={<Support />} />
                </Route>

                {/* Admin / Webmaster */}
                <Route element={<AdminLayout />}>
                  <Route path="/admin"            element={<AdminOverview />} />
                  <Route path="/admin/users"      element={<AdminUsers />} />
                  <Route path="/admin/quotes"     element={<AdminQuotes />} />
                  <Route path="/admin/policies"   element={<AdminPolicies />} />
                  <Route path="/admin/messages"   element={<AdminMessages />} />
                  <Route path="/admin/content"    element={<AdminContent />} />
                  <Route path="/admin/blog"       element={<AdminBlog />} />
                  <Route path="/admin/insurers"   element={<AdminInsurers />} />
                  <Route path="/admin/campaigns"  element={<AdminCampaignReport />} />
                  <Route path="/admin/newsletter" element={<AdminNewsletter />} />
                  <Route path="/admin/seo"        element={<AdminSEO />} />
                  <Route path="/admin/security"   element={<AdminSecurity />} />
                  <Route path="/admin/storage"    element={<AdminStorage />} />
                </Route>
              </Routes>
            </AuthProvider>
          </I18nProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
